import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import SubscriptionForm from '@/components/Form/SubscriptionForm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSubscriptionTypes } from '@/services/api/subscriptions';
import { MealListItem } from '@/domains';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  userId: string;
  initialData?: {
    id: string;
    title?: string;
    includedAmount: number;
    remainingAmount: number;
    paid: boolean;
  };
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

const SubscriptionDialog: React.FC<Props> = ({ open, onOpenChange, mode, userId, initialData, onSuccess, trigger }) => {
  const [selectedType, setSelectedType] = React.useState<MealListItem | null>(initialData?.title ? null : null);
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<MealListItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (open && mode === 'create') {
      setLoading(true);
      getSubscriptionTypes()
        .then((response) => {
          setSubscriptionTypes(response.content);
          if (initialData?.title) {
            const type = response.content.find((t) => t.title === initialData.title);
            setSelectedType(type || null);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [open, mode, initialData?.title]);

  const handleTypeChange = (typeId: string) => {
    const type = subscriptionTypes.find((t) => t.id === typeId);
    setSelectedType(type || null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add new subscription' : 'Update subscription'}</DialogTitle>
        </DialogHeader>

        {mode === 'create' ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subscription Type</label>
              <Select value={selectedType?.id} onValueChange={handleTypeChange} disabled={loading}>
                <SelectTrigger>
                  <SelectValue placeholder={loading ? 'Loading...' : 'Select a subscription type'} />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <SubscriptionForm
              userId={userId}
              initialData={{
                id: selectedType?.id || '',
                includedAmount: 10,
                remainingAmount: 10,
                paid: true
              }}
              mode="create"
              success={onSuccess}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subscription Type</label>
              <div className="text-sm text-muted-foreground">{initialData?.title}</div>
            </div>

            <SubscriptionForm userId={userId} initialData={initialData} mode="edit" success={onSuccess} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionDialog;
